import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Upload } from "lucide-react";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";
function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

type SalesProduct = {
  productId: string;
  name: string;
  sku: string | null;
  qtySold: number;
  sellingPrice: number | null;
  value: number | null;
};
type SalesGroup = { groupName: string; products: SalesProduct[] };
type TeamSalesResponse = {
  team: { id: string; name: string } | null;
  groups: SalesGroup[];
};
type BankSlip = { id: string; imageUrl: string; s3Key: string; createdAt?: string };
type LocalBankSlip = { localId: string; file: File; previewUrl: string };
type PendingPayload = {
  salesDate: string;
  lines: Array<{ productId: string; qtySold: number }>;
  groups: SalesGroup[];
  requiresReason?: boolean;
};
const SALES_PENDING_CONFIRM_KEY = "salesPendingConfirmPayload";
const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

function formatAtmAmountFromDigits(digits: string) {
  const onlyDigits = digits.replace(/\D/g, "");
  const normalized = onlyDigits.length ? onlyDigits : "0";
  const value = Number(normalized) / 100;
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function SalesConfirm() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [sales, setSales] = useState<TeamSalesResponse | null>(null);
  const [pending, setPending] = useState<PendingPayload | null>(null);
  const [bankSlips, setBankSlips] = useState<BankSlip[]>([]);
  const [localBankSlips, setLocalBankSlips] = useState<LocalBankSlip[]>([]);
  const [bankInAmountDigits, setBankInAmountDigits] = useState("");
  const [reason, setReason] = useState("");
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [showMismatchConfirm, setShowMismatchConfirm] = useState(false);
  const [mismatchDiffCents, setMismatchDiffCents] = useState(0);
  const [brokenPreviewKeys, setBrokenPreviewKeys] = useState<Record<string, boolean>>({});

  const todayKey = useMemo(() => malaysiaDateKeyFromDate(new Date()), []);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    for (const g of pending?.groups ?? []) {
      for (const p of g.products ?? []) {
        const qty = p.qtySold ?? 0;
        const price = p.sellingPrice ?? null;
        totalQty += qty;
        if (typeof price === "number") totalValue += qty * price;
      }
    }
    return { totalQty, totalValue };
  }, [pending]);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError("Not authenticated. Please login again.");
        return;
      }
      const rawPending = sessionStorage.getItem(SALES_PENDING_CONFIRM_KEY);
      if (!rawPending) {
        setError("No staged sales found. Please submit from Sales page first.");
        return;
      }
      const parsedPending = JSON.parse(rawPending) as PendingPayload;
      setPending(parsedPending);
      setSales({ team: null, groups: parsedPending.groups });
      if (parsedPending.requiresReason) setShowReasonInput(true);

      setLoading(true);
      setError("");
      try {
        const proofRes = await fetch(`${apiUrl}/supervisor/team-sales/bank-proof-today`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const proofJson: any = await proofRes.json().catch(() => ({}));
        if (proofRes.ok) {
          const slips = Array.isArray(proofJson?.sales?.bankSlips) ? proofJson.sales.bankSlips : [];
          setBankSlips(slips.map((s: any) => ({ id: s.id, imageUrl: s.imageUrl, s3Key: s.s3Key, createdAt: s.createdAt })));
          if (typeof proofJson?.sales?.bankInAmount === "number") {
            const cents = Math.max(0, Math.round(proofJson.sales.bankInAmount * 100));
            setBankInAmountDigits(String(cents));
          }
        }
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [apiUrl, token, todayKey]);

  useEffect(() => {
    return () => {
      // Prevent object URL leaks when leaving this screen.
      localBankSlips.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    };
  }, [localBankSlips]);

  const handleSelectImage = (file: File) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_MIME_TYPES.has(file.type)) {
      setError("Unsupported image format. Please use JPG, PNG, GIF, or WEBP.");
      setSuccess("");
      return;
    }
    setError("");
    const previewUrl = URL.createObjectURL(file);
    setLocalBankSlips((prev) => [
      { localId: `${Date.now()}-${Math.random()}`, file, previewUrl },
      ...prev,
    ]);
    setSuccess("Image ready for confirmation.");
  };

  const removeLocalSlip = (localId: string) => {
    setLocalBankSlips((prev) => {
      const target = prev.find((s) => s.localId === localId);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        if (previewImageUrl === target.previewUrl) setPreviewImageUrl(null);
      }
      return prev.filter((s) => s.localId !== localId);
    });
    setBrokenPreviewKeys((prev) => {
      const next = { ...prev };
      delete next[localId];
      return next;
    });
  };

  const removeSavedSlip = (id: string) => {
    setBankSlips((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target && previewImageUrl === target.imageUrl) setPreviewImageUrl(null);
      return prev.filter((s) => s.id !== id);
    });
    setBrokenPreviewKeys((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const doSaveBankIn = async () => {
    if (!token) return;
    if (!pending?.lines?.length) {
      setError("No staged sales lines found. Please submit from Sales page again.");
      return;
    }
    if (showReasonInput && !reason.trim()) {
      setError("Please state your reason for resubmission.");
      return;
    }
    const amount = Number(bankInAmountDigits || "0") / 100;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Please enter a valid Bank In Amount.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Upload selected local images only when user confirms.
      const uploadedLocalSlips: Array<{ imageUrl: string; s3Key: string }> = [];
      for (const slip of localBankSlips) {
        const formData = new FormData();
        formData.append("image", slip.file);
        const uploadRes = await fetch(`${apiUrl}/supervisor/team-sales/upload-bank-slip-today`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const uploadJson: any = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          setError(uploadJson?.message || `Upload failed (${uploadRes.status}).`);
          return;
        }
        const created = uploadJson?.data;
        if (created?.imageUrl && created?.s3Key) {
          uploadedLocalSlips.push({ imageUrl: created.imageUrl, s3Key: created.s3Key });
        }
      }

      const finalBankSlips = [
        ...bankSlips.map((s) => ({ imageUrl: s.imageUrl, s3Key: s.s3Key })),
        ...uploadedLocalSlips,
      ];

      const res = await fetch(`${apiUrl}/supervisor/team-sales/confirm-bank-in-today`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bankInAmount: amount,
          reason: reason.trim() ? reason.trim() : undefined,
          lines: pending.lines,
          bankSlips: finalBankSlips,
        }),
      });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = json?.message || `Save failed (${res.status}).`;
        setError(message);
        if (typeof message === "string" && message.toLowerCase().includes("reason is required for resubmission")) {
          setShowReasonInput(true);
        }
        return;
      }
      sessionStorage.removeItem(SALES_PENDING_CONFIRM_KEY);
      setShowReasonInput(false);
      // Clear local previews after successful save.
      setLocalBankSlips((prev) => {
        prev.forEach((s) => URL.revokeObjectURL(s.previewUrl));
        return [];
      });
      navigate("/app/sales/success");
    } catch {
      setError("Unable to save bank-in proof. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBankIn = async () => {
    const amount = Number(bankInAmountDigits || "0") / 100;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Please enter a valid Bank In Amount.");
      return;
    }
    const totalCents = Math.round((totals.totalValue || 0) * 100);
    const amountCents = Math.round(amount * 100);
    const diff = amountCents - totalCents;

    // Only warn when there is a real difference (>= 1 cent).
    if (diff !== 0) {
      setMismatchDiffCents(diff);
      setShowMismatchConfirm(true);
      return;
    }

    await doSaveBankIn();
  };

  const formatCentsToRM = (cents: number) => {
    const abs = Math.abs(cents);
    const v = abs / 100;
    return `RM ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50">
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/app/sales")}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <div className="text-[18px] font-semibold">Sales Submission Summary</div>
            <div className="w-10 h-10" />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 pb-28">
        {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div> : null}
        {success ? <div className="mb-3 rounded-lg border border-green-200 bg-green-50 text-green-700 px-3 py-2 text-sm">{success}</div> : null}

        <div className="rounded-xl border border-gray-200 bg-white p-3 mb-3">
          <div className="text-sm font-semibold text-slate-900 mb-2">Today Sales Summary</div>
          {loading ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : (
            <>
              {(sales?.groups ?? []).map((g) => (
                <div key={g.groupName} className="mb-2">
                  <div className="text-base font-bold text-slate-900 mb-1">{g.groupName}</div>
                  {(g.products ?? [])
                    .filter((p) => (p.qtySold ?? 0) > 0)
                    .map((p) => (
                      <div key={p.productId} className="flex items-center justify-between text-sm py-0.5">
                        <div className="min-w-0 truncate">{p.name}</div>
                        <div className="tabular-nums text-slate-700">{p.qtySold}</div>
                      </div>
                    ))}
                </div>
              ))}

              <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Total Pack Sold</div>
                  <div className="text-base font-semibold text-slate-900 tabular-nums">{totals.totalQty.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Total Value (RM)</div>
                  <div className="text-base font-semibold text-slate-900 tabular-nums">
                    {`RM ${totals.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-sm font-semibold text-slate-900 mb-2">Bank Slip / Transfer Slip</div>
          <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 cursor-pointer hover:bg-gray-50">
            <Upload size={16} />
            <span>Select Image</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleSelectImage(file);
                e.currentTarget.value = "";
              }}
            />
          </label>

          {localBankSlips.length || bankSlips.length ? (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {localBankSlips.map((s) => (
                <div key={s.localId} className="relative rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <button
                    type="button"
                    className="absolute top-1 right-1 z-20 rounded-full bg-black/70 text-white w-6 h-6 flex items-center justify-center text-sm"
                    onClick={() => removeLocalSlip(s.localId)}
                    aria-label="Remove selected image"
                  >
                    ×
                  </button>
                  <button
                    type="button"
                    className="block w-full"
                    onClick={() => setPreviewImageUrl(s.previewUrl)}
                    aria-label="Open full image preview"
                  >
                    {brokenPreviewKeys[s.localId] ? (
                      <div className="w-full h-24 bg-gray-50 flex items-center justify-center text-[11px] text-slate-500 px-2 text-center">
                        Preview unavailable
                      </div>
                    ) : (
                      <img
                        src={s.previewUrl}
                        alt="Selected bank slip preview"
                        className="w-full h-24 object-contain bg-gray-50"
                        onError={() => {
                          setBrokenPreviewKeys((prev) => ({ ...prev, [s.localId]: true }));
                          setError("Unable to preview this image format. Please use JPG, PNG, GIF, or WEBP.");
                        }}
                      />
                    )}
                  </button>
                </div>
              ))}
              {bankSlips.map((s) => (
                <div key={s.id} className="relative rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <button
                    type="button"
                    className="absolute top-1 right-1 z-20 rounded-full bg-black/70 text-white w-6 h-6 flex items-center justify-center text-sm"
                    onClick={() => removeSavedSlip(s.id)}
                    aria-label="Remove bank slip"
                  >
                    ×
                  </button>
                  <button
                    type="button"
                    className="block w-full"
                    onClick={() => setPreviewImageUrl(s.imageUrl)}
                    aria-label="Open full image preview"
                  >
                    {brokenPreviewKeys[s.id] ? (
                      <div className="w-full h-24 bg-gray-50 flex items-center justify-center text-[11px] text-slate-500 px-2 text-center">
                        Preview unavailable
                      </div>
                    ) : (
                      <img
                        src={s.imageUrl}
                        alt="Bank slip"
                        className="w-full h-24 object-contain bg-gray-50"
                        onError={() => setBrokenPreviewKeys((prev) => ({ ...prev, [s.id]: true }))}
                      />
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-sm text-slate-500 flex items-center gap-2">
              <ImageIcon size={16} />
              <span>No bank slip uploaded yet.</span>
            </div>
          )}

          <div className="mt-4">
            <div className="text-sm font-semibold text-slate-900 mb-1">Bank In Amount</div>
            <input
              type="text"
              inputMode="numeric"
              className="w-1/2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none tabular-nums"
              placeholder="0.00"
              value={formatAtmAmountFromDigits(bankInAmountDigits)}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                setBankInAmountDigits(digitsOnly);
              }}
            />
          </div>

          {showReasonInput ? (
            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-900 mb-1">Resubmission Reason</div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none"
                placeholder="Please state your reason for resubmission"
              />
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => void handleSaveBankIn()}
            disabled={saving}
            className="mt-4 w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
          >
            {saving ? "Saving..." : "Submit Sales"}
          </button>
        </div>
      </div>

      {previewImageUrl ? (
        <button
          type="button"
          className="fixed inset-0 z-[120] bg-black/75 p-4 flex items-center justify-center"
          onClick={() => setPreviewImageUrl(null)}
          aria-label="Close image preview"
        >
          <img
            src={previewImageUrl}
            alt="Full bank slip preview"
            className="max-w-full max-h-full object-contain rounded-lg bg-white"
          />
        </button>
      ) : null}

      {showMismatchConfirm ? (
        <div className="fixed inset-0 z-[130] bg-black/60 flex items-end justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-base font-semibold text-slate-900">Amount mismatch</div>
              <div className="text-sm text-slate-600 mt-1">
                Your <span className="font-semibold">Bank In Amount</span> does not match <span className="font-semibold">Total Value (RM)</span>.
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Total Value (RM)</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {`RM ${totals.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Bank In Amount</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {formatAtmAmountFromDigits(bankInAmountDigits)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Difference</span>
                <span
                  className={`font-bold tabular-nums ${mismatchDiffCents === 0 ? "text-slate-900" : mismatchDiffCents > 0 ? "text-amber-700" : "text-red-700"}`}
                >
                  {mismatchDiffCents > 0 ? `+${formatCentsToRM(mismatchDiffCents)}` : `-${formatCentsToRM(mismatchDiffCents)}`}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                If you proceed, this difference will be saved for audit.
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-slate-700"
                onClick={() => setShowMismatchConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-xl bg-primary text-white py-2.5 text-sm font-semibold disabled:opacity-60"
                onClick={() => {
                  setShowMismatchConfirm(false);
                  void doSaveBankIn();
                }}
                disabled={saving}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
